import { Container, LazyLottie, VideoAnimation, WithGradientedName } from '@/components';
import { Box, Typography } from '@mui/material';
import { CSSProperties, FC, ReactElement } from 'react';
import styles from './styles.module.scss';
import Image, { StaticImageData } from 'next/image';

export enum FeaturesSectionItemType {
  Image = 'image',
  Animation = 'animation',
  Video = 'video',
}

type FeaturesSectionItemImage = FeaturesSectionItem & {
  imageSrc: StaticImageData;
  type: FeaturesSectionItemType.Image;
};

type FeaturesSectionItem = {
  id: string;
  title: string | ReactElement;
  text: string | ReactElement;
};

type FeaturesSectionItemAnimation = FeaturesSectionItem & {
  animationSrc: string;
  previewSrc: string | StaticImageData;
  type: FeaturesSectionItemType.Animation;
  previewStyles?: CSSProperties;
  animationStyles?: CSSProperties;
};

type FeaturesSectionItemVideo = FeaturesSectionItem & {
  animationSrc: string;
  previewSrc: string;
  type: FeaturesSectionItemType.Video;
};

type FeaturesSectionItems = Array<
  FeaturesSectionItemImage | FeaturesSectionItemAnimation | FeaturesSectionItemVideo
>;

type FeaturesSectionProps = {
  title: string;
  items: FeaturesSectionItems;
};

export const FeaturesSection: FC<FeaturesSectionProps> = ({ title, items }) => {
  return (
    <Box className={styles.section} component="section">
      <Container>
        <Box className={styles.body}>
          <Typography variant="h2" className={styles.title}>
            <WithGradientedName text={title} lastLetterGradient />
          </Typography>
          {items.map((item) => {
            return (
              <Box key={item.id} className={styles.item}>
                <Box className={styles.itemInfo}>
                  <Typography variant="h4" className={styles.itemInfoTitle}>
                    {item.title}
                  </Typography>
                  <Typography variant="body1" className={styles.itemInfoText}>
                    {item.text}
                  </Typography>
                </Box>
                <Box className={styles.itemImg}>
                  {item.type === FeaturesSectionItemType.Image && (
                    <Image alt="" src={item.imageSrc} />
                  )}
                  {item.type === FeaturesSectionItemType.Animation && (
                    <Box style={item.previewStyles}>
                      <LazyLottie
                        animationStyles={item.animationStyles}
                        dataSrc={item.animationSrc}
                        previewSrc={item.previewSrc}
                        previewProps={{ alt: '', loading: 'lazy' }}
                      />
                    </Box>
                  )}
                  {item.type === FeaturesSectionItemType.Video && (
                    <VideoAnimation src={item.animationSrc} poster={item.previewSrc} />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};
